import React from 'react';

/**
 * 在職状態での絞り込み。
 *
 * 従業員を並べる画面すべてで同じ形にする。画面ごとに「退職者を出す・出さない」を
 * 決め打ちすると、**日々の操作では邪魔になり、記録を追うときには足りない**という
 * 両方の不満が同時に出る。どちらが要るかはその時の目的で決まるので、
 * 選べる形にして既定だけ在職者にしておく。
 */
export type Employment = 'active' | 'retired' | 'all';

export const EMPLOYMENT_LABELS: Record<Employment, string> = {
  active: '在職者',
  retired: '退職者',
  all: 'すべて',
};

interface Props {
  value: Employment;
  onChange: (value: Employment) => void;
  /** 件数を添える（「0人表示中」の理由が絞り込みだと分かるように） */
  count?: number;
  id?: string;
  /**
   * 並べ方。周囲の入力欄に合わせる。
   * - `inline`（既定）: ラベルを左に置く。単独で置かれる画面向け。
   * - `stacked`: ラベルを上に置く。部署・検索などが縦積みで並ぶ絞り込み行に混ぜるとき、
   *   これを使わないと**この項目だけ段が下がって浮く**。
   */
  layout?: 'inline' | 'stacked';
}

const EmploymentFilter: React.FC<Props> = ({
  value,
  onChange,
  count,
  id = 'employmentFilter',
  layout = 'inline',
}) => (
  <div
    style={
      layout === 'stacked'
        ? { display: 'block' }
        : { display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }
    }
  >
    <label
      htmlFor={id}
      style={
        layout === 'stacked'
          ? { display: 'block', marginBottom: '4px', fontSize: 'clamp(11px, 2.5vw, 13px)', color: '#6c757d' }
          : { fontSize: 'clamp(12px, 2.5vw, 13px)', color: '#6c757d', whiteSpace: 'nowrap' }
      }
    >
      在職状態{layout === 'stacked' && count !== undefined ? `（${count}人）` : ''}
    </label>
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value as Employment)}
      style={{
        width: layout === 'stacked' ? '100%' : undefined,
        padding: layout === 'stacked'
          ? 'clamp(6px, 1.5vw, 8px) clamp(10px, 2.5vw, 12px)'
          : 'clamp(5px, 1.5vw, 7px) clamp(8px, 2vw, 10px)',
        border: layout === 'stacked' ? '1px solid #ced4da' : '1px solid #dee2e6',
        borderRadius: '4px',
        fontSize: 'clamp(12px, 2.5vw, 14px)',
        background: '#fff',
      }}
    >
      {(Object.keys(EMPLOYMENT_LABELS) as Employment[]).map((key) => (
        <option key={key} value={key}>
          {EMPLOYMENT_LABELS[key]}
        </option>
      ))}
    </select>
    {layout === 'inline' && count !== undefined && (
      <span style={{ fontSize: 'clamp(11px, 2vw, 12px)', color: '#6c757d', whiteSpace: 'nowrap' }}>
        {count}人
      </span>
    )}
  </div>
);

export default EmploymentFilter;
